import { useEffect, useState } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { supabase } from "@/integrations/supabase/client";
import { formatEuro } from "@/lib/catalog";
import { Package, ChevronDown, ChevronUp, ShoppingBag, ArrowRight, Download, Check, Loader2 } from "lucide-react";
import { useAuth } from "@/hooks/use-auth";
import { downloadOrderPdf, DEFAULT_PAYMENT_TERM_DAYS } from "@/lib/order-pdf";
import { updateOrderPaymentTerm } from "@/lib/orders.functions";
import {
  validatePaymentTermDays,
  isPaymentTermDaysValid,
  PAYMENT_TERM_ERROR_MSG,
} from "@/lib/payment-term";

const PAYMENT_TERM_OPTIONS = [7, 14, 30, 60, 90];


export const Route = createFileRoute("/_authenticated/account/bestellingen")({
  head: () => ({ meta: [{ title: "Mijn bestellingen — Elvion Group Nederland" }, { name: "robots", content: "noindex" }] }),
  component: OrdersPage,
});

type LineItem = {
  slug: string;
  name: string;
  qty: number;
  list_price_cents: number;
  unit_price_cents: number;
  line_total_cents: number;
};

type ShippingAddress = {
  full_name: string | null;
  company: string | null;
  street: string;
  house_number: string | null;
  postal_code: string;
  city: string;
  country: string | null;
};

type Order = {
  id: string;
  order_number: string;
  status: "pending" | "processing" | "completed" | "cancelled";
  total_cents: number;
  created_at: string;
  line_items: LineItem[];
  notes: string | null;
  payment_term_days: number | null;
  shipping_address: ShippingAddress | null;
};


const statusConfig: Record<Order["status"], { label: string; bg: string; text: string; border: string }> = {
  pending: { label: "In afwachting", bg: "bg-yellow-50", text: "text-yellow-800", border: "border-yellow-200" },
  processing: { label: "In behandeling", bg: "bg-blue-50", text: "text-blue-800", border: "border-blue-200" },
  completed: { label: "Afgerond", bg: "bg-green-50", text: "text-green-800", border: "border-green-200" },
  cancelled: { label: "Geannuleerd", bg: "bg-[#3a7bd5]", text: "text-[#3a7bd5]", border: "border-[#3a7bd5]" },
};

function StatusBadge({ status }: { status: Order["status"] }) {
  const c = statusConfig[status];
  return (
    <span className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-medium ${c.bg} ${c.text} ${c.border}`}>
      {c.label}
    </span>
  );
}

function OrdersPage() {
  const [openId, setOpenId] = useState<string | null>(null);
  const [termsByOrder, setTermsByOrder] = useState<Record<string, number>>({});
  const [customTermsByOrder, setCustomTermsByOrder] = useState<Record<string, string>>({});
  const [useCustomByOrder, setUseCustomByOrder] = useState<Record<string, boolean>>({});
  const [termErrors, setTermErrors] = useState<Record<string, string>>({});
  const [savingByOrder, setSavingByOrder] = useState<Record<string, boolean>>({});
  const [savedByOrder, setSavedByOrder] = useState<Record<string, boolean>>({});
  const { user, profile } = useAuth();
  const saveTermFn = useServerFn(updateOrderPaymentTerm);

  const { data, isLoading, refetch } = useQuery({
    queryKey: ["my-orders"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("orders")
        .select(
          "id, order_number, status, total_cents, created_at, line_items, notes, payment_term_days, shipping_address:addresses(street, house_number, postal_code, city, country)"
        )
        .order("created_at", { ascending: false })
        .returns<Order[]>();
      if (error) throw error;
      return data ?? [];
    },
  });

  // Seed local term state from server values when orders load
  useEffect(() => {
    if (!data) return;
    setTermsByOrder((prev) => {
      const next = { ...prev };
      for (const o of data) {
        if (next[o.id] === undefined && o.payment_term_days != null) {
          next[o.id] = o.payment_term_days;
        }
      }
      return next;
    });
  }, [data]);


  const TERM_ERROR_MSG = PAYMENT_TERM_ERROR_MSG;

  const validateCustomTerm = (orderId: string, value: string): number | null => {
    const result = validatePaymentTermDays(value);
    if (!result.valid) {
      setTermErrors((prev) => ({ ...prev, [orderId]: result.error }));
      return null;
    }
    setTermErrors((prev) => {
      const next = { ...prev };
      delete next[orderId];
      return next;
    });
    return result.value;
  };

  const getEffectiveTerm = (orderId: string): number => {
    if (useCustomByOrder[orderId]) {
      const result = validatePaymentTermDays(customTermsByOrder[orderId] ?? "");
      if (result.valid) return result.value;
    }
    return termsByOrder[orderId] ?? DEFAULT_PAYMENT_TERM_DAYS;
  };

  // Strict validity check used to gate the "Save term" submit (no fallbacks).
  const isTermStrictlyValid = (orderId: string): boolean => {
    if (useCustomByOrder[orderId]) {
      return isPaymentTermDaysValid(customTermsByOrder[orderId] ?? "");
    }
    return isPaymentTermDaysValid(
      termsByOrder[orderId] ?? DEFAULT_PAYMENT_TERM_DAYS,
    );
  };

  const handleSaveTerm = async (orderId: string) => {
    // Block submit unless the value is strictly a valid integer in [1, 365].
    if (!isTermStrictlyValid(orderId)) {
      if (useCustomByOrder[orderId]) {
        validateCustomTerm(orderId, customTermsByOrder[orderId] ?? "");
      } else {
        setTermErrors((p) => ({ ...p, [orderId]: TERM_ERROR_MSG }));
      }
      return;
    }
    const term = useCustomByOrder[orderId]
      ? Number((customTermsByOrder[orderId] ?? "").trim())
      : (termsByOrder[orderId] ?? DEFAULT_PAYMENT_TERM_DAYS);

    setSavingByOrder((p) => ({ ...p, [orderId]: true }));
    setSavedByOrder((p) => ({ ...p, [orderId]: false }));
    try {
      await saveTermFn({ data: { orderId, paymentTermDays: term } });
      setTermErrors((p) => {
        const next = { ...p };
        delete next[orderId];
        return next;
      });
      setSavedByOrder((p) => ({ ...p, [orderId]: true }));
      await refetch();
      setTimeout(() => setSavedByOrder((p) => ({ ...p, [orderId]: false })), 2000);
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Opslaan mislukt";
      // Surface server-side validation message under the field
      setTermErrors((p) => ({ ...p, [orderId]: `Server weigerde de waarde: ${msg}` }));
    } finally {
      setSavingByOrder((p) => ({ ...p, [orderId]: false }));
    }
  };


  const handleDownload = (order: Order) => {
    if (useCustomByOrder[order.id]) {
      const valid = validateCustomTerm(order.id, customTermsByOrder[order.id] ?? "");
      if (valid === null) return;
    }
    const term = getEffectiveTerm(order.id);

    downloadOrderPdf(
      {
        order_number: order.order_number,
        status: order.status,
        total_cents: order.total_cents,
        created_at: order.created_at,
        line_items: order.line_items ?? [],
        notes: order.notes,
        shipping_address: order.shipping_address,
        customer: {
          full_name: profile?.full_name ?? null,
          email: user?.email ?? null,
        },
      },
      term,
    );
  };


  if (isLoading) {
    return (
      <div className="space-y-6">
        <h1 className="text-2xl font-bold">Mijn bestellingen</h1>
        <div className="space-y-3">
          {Array.from({ length: 3 }).map((_, i) => (
            <div key={i} className="h-20 animate-pulse rounded-xl bg-muted" />
          ))}
        </div>
      </div>
    );
  }

  if (!data || data.length === 0) {
    return (
      <div className="space-y-6">
        <h1 className="text-2xl font-bold">Mijn bestellingen</h1>
        <div className="flex flex-col items-center justify-center rounded-xl border border-border bg-card p-12 text-center">
          <ShoppingBag className="size-12 text-text-secondary" />
          <h2 className="mt-4 text-lg font-semibold">Nog geen bestellingen</h2>
          <p className="mt-1 max-w-sm text-sm text-text-secondary">
            Je hebt nog niets besteld. Ontdek ons assortiment en plaats je eerste bestelling.
          </p>
          <Link
            to="/c"
            className="mt-6 inline-flex items-center gap-2 rounded-md bg-orange px-4 py-2 text-sm font-medium text-white hover:bg-orange/90"
          >
            Naar de webshop <ArrowRight className="size-4" />
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold">Mijn bestellingen</h1>
        <span className="text-sm text-text-secondary">{data.length} bestelling{data.length === 1 ? "" : "en"}</span>
      </div>

      <div className="space-y-3">
        {data.map((order) => {
          const isOpen = openId === order.id;
          return (
            <div
              key={order.id}
              className={`overflow-hidden rounded-xl border bg-card transition-shadow ${isOpen ? "shadow-md ring-1 ring-orange/20" : "hover:shadow-sm"}`}
            >
              <button
                onClick={() => setOpenId(isOpen ? null : order.id)}
                className="flex w-full items-center justify-between gap-4 p-4 text-left sm:p-5"
              >
                <div className="flex items-center gap-4">
                  <div className="flex size-10 shrink-0 items-center justify-center rounded-full bg-muted">
                    <Package className="size-5 text-text-secondary" />
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="font-semibold">#{order.order_number}</span>
                      <StatusBadge status={order.status} />
                    </div>
                    <div className="mt-0.5 text-xs text-text-secondary">
                      {new Date(order.created_at).toLocaleDateString("nl-NL", {
                        weekday: "long",
                        year: "numeric",
                        month: "long",
                        day: "numeric",
                      })}
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <span className="hidden text-right sm:block">
                    <span className="block text-sm font-semibold">{formatEuro(order.total_cents)}</span>
                    <span className="text-xs text-text-secondary">{order.line_items?.length ?? 0} product{order.line_items?.length === 1 ? "" : "en"}</span>
                  </span>
                  {isOpen ? <ChevronUp className="size-5 text-text-secondary" /> : <ChevronDown className="size-5 text-text-secondary" />}
                </div>
              </button>

              {isOpen && (
                <div className="border-t bg-background/50 px-4 pb-5 pt-4 sm:px-5">
                  <div className="space-y-3">
                    {order.line_items?.map((item, idx) => (
                      <div
                        key={`${item.slug}-${idx}`}
                        className="flex items-center justify-between gap-3 rounded-lg border p-3"
                      >
                        <div className="min-w-0">
                          <p className="truncate text-sm font-medium">{item.name}</p>
                          <p className="mt-0.5 text-xs text-text-secondary">
                            {item.qty} × {formatEuro(item.unit_price_cents)}
                          </p>
                        </div>
                        <span className="shrink-0 text-sm font-semibold">{formatEuro(item.line_total_cents)}</span>
                      </div>
                    ))}
                  </div>

                  <div className="mt-4 space-y-1 border-t pt-4 text-sm">
                    <div className="flex justify-between text-text-secondary">
                      <span>Totaal</span>
                      <span className="font-semibold text-foreground">{formatEuro(order.total_cents)}</span>
                    </div>
                    {order.notes && (
                      <div className="mt-2 rounded-lg bg-yellow-50 p-3 text-xs text-yellow-800">
                        <span className="font-semibold">Opmerking:</span> {order.notes}
                      </div>
                    )}
                  </div>

                  <div className="mt-4 flex flex-wrap items-center justify-end gap-3">
                    <label className="flex flex-wrap items-center gap-2 text-sm text-text-secondary">
                      <span>Betaaltermijn</span>
                      <select
                        value={useCustomByOrder[order.id] ? "custom" : (termsByOrder[order.id] ?? DEFAULT_PAYMENT_TERM_DAYS)}
                        onChange={(e) => {
                          const val = e.target.value;
                          if (val === "custom") {
                            setUseCustomByOrder((prev) => ({ ...prev, [order.id]: true }));
                          } else {
                            setUseCustomByOrder((prev) => ({ ...prev, [order.id]: false }));
                            setTermsByOrder((prev) => ({ ...prev, [order.id]: Number(val) }));
                          }
                        }}
                        className="rounded-md border border-border bg-card px-2 py-1.5 text-sm font-medium text-foreground focus:outline-none focus:ring-2 focus:ring-orange/30"
                      >
                        {PAYMENT_TERM_OPTIONS.map((d) => (
                          <option key={d} value={d}>
                            {d} dagen{d === DEFAULT_PAYMENT_TERM_DAYS ? " (standaard)" : ""}
                          </option>
                        ))}
                        <option value="custom">Aangepast…</option>
                      </select>
                      {useCustomByOrder[order.id] && (
                        <div className="flex flex-col gap-1">
                          <input
                            type="number"
                            min={1}
                            max={365}
                            value={customTermsByOrder[order.id] ?? ""}
                            onChange={(e) => {
                              setCustomTermsByOrder((prev) => ({ ...prev, [order.id]: e.target.value }));
                              validateCustomTerm(order.id, e.target.value);
                            }}
                            onBlur={(e) => validateCustomTerm(order.id, e.target.value)}
                            placeholder="Dagen"
                            className={`w-24 rounded-md border bg-card px-2 py-1.5 text-sm font-medium text-foreground focus:outline-none focus:ring-2 ${termErrors[order.id] ? "border-[#3a7bd5] focus:ring-[#3a7bd5]" : "border-border focus:ring-orange/30"}`}
                          />
                          {termErrors[order.id] && (
                            <span className="text-xs text-[#3a7bd5]">{termErrors[order.id]}</span>
                          )}
                        </div>
                      )}
                      {!useCustomByOrder[order.id] && termErrors[order.id] && (
                        <span role="alert" className="text-xs text-[#3a7bd5] basis-full">{termErrors[order.id]}</span>
                      )}
                    </label>

                    <button
                      onClick={() => handleSaveTerm(order.id)}
                      disabled={savingByOrder[order.id]}
                      className="inline-flex items-center gap-2 rounded-md border border-border bg-card px-3 py-2 text-sm font-medium hover:bg-muted disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      {savingByOrder[order.id] ? (
                        <Loader2 className="size-4 animate-spin" />
                      ) : savedByOrder[order.id] ? (
                        <Check className="size-4 text-green-600" />
                      ) : null}
                      {savedByOrder[order.id] ? "Opgeslagen" : savingByOrder[order.id] ? "Opslaan…" : "Termijn opslaan"}
                    </button>
                    <button
                      onClick={() => handleDownload(order)}
                      disabled={useCustomByOrder[order.id] && !!termErrors[order.id]}
                      className="inline-flex items-center gap-2 rounded-md border border-border bg-card px-3 py-2 text-sm font-medium hover:bg-muted disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      <Download className="size-4" />
                      Factuur (PDF)
                    </button>
                    <Link
                      to="/account/bestellingen/$id"
                      params={{ id: order.id }}
                      className="inline-flex items-center gap-2 rounded-md bg-[#3a7bd5] px-3 py-2 text-sm font-semibold text-white hover:bg-[#2f6ac0]"
                    >
                      Bekijk details <ArrowRight className="size-4" />
                    </Link>

                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

